import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { compareOptions as compOptions, modalitiesOptions } from './mocks';
import { Option, Selector, SelectorMulti } from '../Selector';
import { DateRangePicker, DateRangeSkeleton } from '../DateRangePicker/DateRangePicker';
import { useDateRangeFilter } from '../../hooks/useCalendarFilter';
import { MultiFilterReportName } from './MultiFilters.types';
import { useMultiFilterStore } from './MultiFilter.store';
import { useUserContext } from '../../context/userContext';
import {
  FullMenuOption,
  makeMenu,
  MultiLevelSelectbox,
} from '../MultiLevelSelectbox/MultiLevelSelectbox';

const optionToValue = (option: Option) => option?.value as string | null;

export const today = new Date().toISOString().substr(0, 10);

type FiltersBarProps = {
  reportName: MultiFilterReportName;
  compareOptions: Option[];
  compareTitle?: string;
  stackByTitle?: string;
  stackByOptions: Option[];
  normalizationTitle?: string;
  normalizationOptions?: Option[];
  categoryOptions?: Option[];
  categoryTitle?: string;
  hideCompareSelectbox?: boolean;
  hideCategoryDropdown?: boolean;
  hideStackByDropdown?: boolean;
  hideNormalizationDropdown?: boolean;
  activitiesViews?: Option[];
  leftSlot?: JSX.Element;
  rightSlot?: JSX.Element;
  alwaysGroupBy?: boolean;
};

export const FiltersBar: FC<FiltersBarProps> = ({
  reportName,
  hideCompareSelectbox,
  hideCategoryDropdown = true,
  hideStackByDropdown = true,
  hideNormalizationDropdown = true,
  compareOptions = compOptions,
  // stackByOptions,
  compareTitle = 'Compare',
  categoryOptions,
  categoryTitle = 'Across',
  stackByTitle = 'Stack',
  normalizationTitle = 'Normalize',
  normalizationOptions,
  activitiesViews,
  alwaysGroupBy,
  leftSlot,
  rightSlot,
}) => {
  const { isInited } = useUserContext();
  const multiFilterStore = useMultiFilterStore();
  const filtersState = multiFilterStore[reportName];
  const { variables, limitRange, setVariables, setRangeDate, setSelectType } = useDateRangeFilter();

  const [localCompareOptions, setLocalCompareOptions] = useState<Option[]>(compareOptions);
  const [localCategoryOptions, setLocalCategoryOptions] = useState<Option[]>(categoryOptions || []);
  const [SelectedCategory, setSelectedCategory] = useState<FullMenuOption>();
  const [SelectCompare, setSelectedCompare] = useState<FullMenuOption>();

  const CategoryGroupOptions: FullMenuOption[] = useMemo(() => {
    const categoryDateRangeDisabled = ['m', 'q', 'y'].includes(filtersState.compare as string);

    return makeMenu([
      ...localCategoryOptions.filter((m) => !['m', 'q', 'y'].includes(m.value as string)),
      {
        label: 'Date Range',
        nested: [
          ...localCategoryOptions.filter((m) => ['m', 'q', 'y'].includes(m.value as string)),
        ],
        value: undefined,
        disabled: categoryDateRangeDisabled,
      },
    ]);
  }, [localCategoryOptions, filtersState]);

  const CompareGroupOptions: FullMenuOption[] = useMemo(() => {
    const compareDateRangeDisabled = ['m', 'q', 'y'].includes(filtersState.category as string);

    return makeMenu([
      ...localCompareOptions.filter((m) => !['m', 'q', 'y'].includes(m.value as string)),
      {
        label: 'Date Range',
        nested: [...localCompareOptions.filter((m) => ['m', 'q', 'y'].includes(m.value as string))],
        value: undefined,
        disabled: compareDateRangeDisabled,
      },
    ]);
  }, [localCompareOptions, filtersState]);

  const setCompare = (option: Option) => {
    const value = optionToValue(option);
    multiFilterStore.setFilters(reportName, 'compare', value);
  };

  // AKA "Across"
  // const setCategory = (option: Option) => {
  //   if (option.value === null) return;

  //   const value = optionToValue(option);
  //   multiFilterStore.setFilters(reportName, 'category', value);
  // };

  const onSelectCategory = useCallback(
    (option: FullMenuOption | undefined) => {
      if (!option || option?.value === null) return;
      setSelectedCategory(option);
      const value = optionToValue(option);
      multiFilterStore.setFilters(reportName, 'category', value);
    },
    [window.location.search, CategoryGroupOptions],
  );

  const onSelectCompare = useCallback(
    (option: FullMenuOption | undefined) => {
      if (!option || option?.value === null) {
        multiFilterStore.setFilters(reportName, 'compare', null);
        setSelectedCompare({ value: null, label: 'None', disabled: false });
        return;
      }
      setSelectedCompare(option);
      const value = optionToValue(option);
      multiFilterStore.setFilters(reportName, 'compare', value);
    },
    [window.location.search, CategoryGroupOptions],
  );

  useEffect(() => {
    // if compare set to procedure and category is also procedure, we need to reset category to OR
    // if (filtersState.category === 'OR' && filtersState.compare === 'OR') {
    //   multiFilterStore.setFilters(reportName, 'category', 'weekday');
    // }

    // If both are the same, set compare to null (Valid selection! Single bars)
    // Only apply this rule when category dropdown is visible
    if (!hideCategoryDropdown && filtersState.category === filtersState.compare) {
      multiFilterStore.setFilters(reportName, 'compare', null);
      setSelectedCompare({ value: null, label: 'None', disabled: false });
    }

    // Disabled month and quarter for localCategoryOptions if compare is month or quarter
    if (['m', 'q'].includes(filtersState.compare as string)) {
      // setLocalCategoryOptions((prev) =>
      //   prev.map((o) =>
      //     ['m', 'q'].includes(o.value as string)
      //       ? { ...o, disabled: true }
      //       : { ...o, disabled: false },
      //   ),
      // );
    } else {
      setLocalCategoryOptions((prev) => prev.map((o) => ({ ...o, disabled: false })));
    }
  }, [filtersState.compare, filtersState.category]);

  useEffect(() => {
    // Older algorithm: if compare set to procedure and category is also procedure, we need to reset category to OR
    // if (filtersState.compare === 'OR' && filtersState.category === 'OR') {
    //   multiFilterStore.setFilters(reportName, 'compare', 'weekday');
    // }

    // If both are the same, set compare to null (Valid selection! Single bars)
    if (filtersState.compare === filtersState.category) {
      multiFilterStore.setFilters(reportName, 'compare', null);
    }

    // Disabled month and quarter for localCompareOptions if category is month or quarter
    if (['m', 'q'].includes(filtersState.category as string)) {
      setLocalCompareOptions((prev) =>
        prev.map((o) =>
          ['m', 'q'].includes(o.value as string)
            ? { ...o, disabled: true }
            : { ...o, disabled: false },
        ),
      );
    } else {
      setLocalCompareOptions((prev) => prev.map((o) => ({ ...o, disabled: false })));
    }
  }, [filtersState.category]);

  useEffect(() => {
    // Initialize default values for essential filters
    multiFilterStore.setFilters(reportName, 'compare', 'OR');
    setSelectedCompare({ value: 'OR', label: 'Rooms', disabled: false });

    multiFilterStore.setFilters(reportName, 'category', 'weekday');
    setSelectedCategory({ value: 'weekday', label: 'Weekdays', disabled: false });
  }, []);

  useEffect(() => {
    // If normalization is not 'None', remove 'After_hours' from stackedBy
    if (filtersState.normalization != 'None' && filtersState.stackedBy?.includes('After_hours')) {
      const excludedStackedBy = filtersState.stackedBy.filter((m) => m !== 'After_hours');
      multiFilterStore.setFilters(reportName, 'stackedBy', excludedStackedBy);
    }
  }, [filtersState.normalization]);

  const setNormalization = (option: Option) => {
    const value = optionToValue(option);

    multiFilterStore.setFilters(reportName, 'normalization', value);
  };

  const setActivitiesViews = (option: Option) => {
    const value = optionToValue(option);

    multiFilterStore.setFilters(reportName, 'activities', value);
  };

  const handleChangeDateRange = useCallback((range: [string, string]) => {
    setSelectType('custom');
    const rangeDate = { from: range[0] || '', to: range[1] || '' };
    setRangeDate(rangeDate);
    setVariables(rangeDate);
  }, []);

  return (
    <div className="mt-10 flex flex-wrap self-center w-full">
      <div className="mr-4">
        <div className="font-bold font-lato text-1214 ml-2 mb-4">Date Range</div>

        {!isInited && <DateRangeSkeleton />}

        {isInited && (
          <DateRangePicker
            loading={!limitRange}
            minDate={limitRange?.from}
            maxDate={limitRange?.to}
            selectType="custom"
            onApply={handleChangeDateRange}
            dates={[variables.from, variables.to]}
          />
        )}
      </div>
      {!hideCompareSelectbox && hideCategoryDropdown && (
        <div className="mr-4">
          <div className="font-bold font-lato text-1214 ml-2 mb-4">{compareTitle}</div>
          <Selector
            onSelect={setCompare}
            options={(localCompareOptions as Option[]).filter((o) => {
              if (alwaysGroupBy) {
                return o.value;
              }

              return o;
            })}
            notFoundLabel="None"
            pickedData={
              alwaysGroupBy && !filtersState.compare
                ? 'procedure'
                : filtersState.compare || undefined
            }
          />
        </div>
      )}
      {!hideCategoryDropdown && (
        <div className="mr-4">
          <div className="font-bold font-lato text-1214 ml-2 mb-4">{compareTitle}</div>

          <MultiLevelSelectbox
            header=""
            shouldHaveNotNestedItems
            loading={false}
            onSelectOption={onSelectCompare}
            selectedItem={SelectCompare}
            options={CompareGroupOptions}
          />
        </div>
      )}
      {!hideCategoryDropdown && (
        <div className="mr-4">
          <div className="font-bold font-lato text-1214 ml-2 mb-4">{categoryTitle}</div>
          <MultiLevelSelectbox
            header=""
            shouldHaveNotNestedItems
            loading={false}
            onSelectOption={onSelectCategory}
            selectedItem={SelectedCategory}
            options={CategoryGroupOptions}
          />
          {/* <Selector
            onSelect={setCategory}
            options={(localCategoryOptions as Option[]).filter((o) => {
              if (alwaysGroupBy) {
                return o.value;
              }

              return o;
            })}
            notFoundLabel="None"
            pickedData={filtersState.category || undefined}
          /> */}
        </div>
      )}
      {/* {!hideStackByDropdown && (
        <div className="mr-4">
          <div className="font-bold font-lato text-1214 ml-2 mb-4">{stackByTitle}</div>
          <SelectorMulti
            onSelect={(options) => {
              const values = options.map(optionToValue);
              multiFilterStore.setFilters(reportName, 'stackedBy', values);
            }}
            options={(modalitiesOptions as Option[]).filter((o) => {
              return o;
            })}
            notFoundLabel="None"
            pickedData={
              filtersState.stackedBy?.length
                ? filtersState?.stackedBy
                : ['Open', 'Lap', 'Robotic', 'Other'] // Default selection (all except After Hours)
            }
          />
        </div>
      )} */}
      {!hideNormalizationDropdown && (
        <div className="mr-4">
          <div className="font-bold font-lato text-1214 ml-2 mb-4">{normalizationTitle}</div>
          <Selector
            onSelect={setNormalization}
            options={(normalizationOptions as Option[]).filter((o) => {
              // if (alwaysGroupBy) {
              //   return o.value;
              // }

              return o;
            })}
            notFoundLabel="None"
            pickedData={filtersState.normalization || 'OR'}
          />
        </div>
      )}
      {!!activitiesViews && (
        <div className="mr-4">
          <div className="font-bold font-lato text-1214 mb-4">Activities</div>
          <Selector
            onSelect={setActivitiesViews}
            options={activitiesViews}
            notFoundLabel="All"
            pickedData={filtersState.activities}
          />
        </div>
      )}
      {leftSlot}
      <div className="flex items-center ml-auto mt-8">{rightSlot}</div>
    </div>
  );
};
